import { useMemo, useState } from "react";
import { UserMinus } from "lucide-react";
import { ActionModal, Button, IconButton } from "@cntrl/ui";
import {
  useAddThreadMembers,
  useRemoveThreadMember,
  type ThreadDetail,
} from "../../app/hooks/useInbox.js";
import { MessageablePicker } from "./MessageablePicker.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { nameInitials } from "../../app/hooks/inboxFormat.js";
import { openPersonProfile } from "../../app/profilePanel.js";
import styles from "./Inbox.module.scss";

export interface GroupMembersModalProps {
  thread: ThreadDetail;
  /** The viewer, so their own row offers Leave rather than Remove — those are different acts. */
  myPersonId?: string;
  /** Raised from the viewer's OWN row. Omitted where leaving is not a thing you may do — a show chat, whose
   *  membership is reconciled from the run, would simply re-seat you on the next sync. */
  onLeave?: () => void;
  onClose: () => void;
}

/**
 * WHO IS IN THIS GROUP CHAT, and changing it (ART-159, PO 2026-09-11).
 *
 * **THE ROSTER IS OPEN AND CHANGING IT IS GATED, which is why the door is no longer** (PO, 2026-09-11).
 * The header used to withhold this dialog entirely unless the caller held `canManageMembers`, so an ordinary
 * member could see five faces and had no way to find out whose they were past the truncation. Who is in the
 * room is a fact about the CHAT — the header already names every one of them for a screen reader — where
 * adding and removing are acts, and it is the acts that carry the verdict: the add picker and the per-row
 * Remove render on the SERVED `canManageMembers` (`core.can_manage_thread_members`: an active participant
 * who also holds the AV tier or is the tour manager), and the route refuses independently. Both halves,
 * because an affordance that is refused and a permission that is unreachable are the same defect twice.
 *
 * **The creator is not special here and that is the ruling, not an omission.** Any AV or TM in the chat has
 * exactly this dialog — the person who started it holds nothing extra, and a tour manager added later holds
 * nothing less. `createdByPersonId` is provenance.
 *
 * **Your own row carries LEAVE, and that is where the header's third button went.** Taking yourself out and
 * being taken out are different acts with different audiences — leaving is open to everybody, removing is
 * not — so they are two labels, but they belong on the same row, because that row is the one place the
 * question "and what about me?" is already being asked. A "Leave" word sitting in the header competed with
 * the chat's own name for the only line it has, and said nothing about who it applied to.
 *
 * **The add list is the caller's OWN reach**, never the chat's: being in a chat with somebody does not let
 * you add their teammates. The server checks the same way, so a person offered here is one it will accept.
 */
export function GroupMembersModal({ thread, myPersonId, onLeave, onClose }: GroupMembersModalProps) {
  const threadId = thread.threadId as string;
  const add = useAddThreadMembers(threadId);
  const remove = useRemoveThreadMember(threadId);
  const [adding, setAdding] = useState<string[]>([]);

  const nameById = useMemo(() => {
    const map = new Map<string, { name: string; avatarMediaId: string | null }>();
    for (const p of thread.people ?? []) {
      if (p.personId) map.set(p.personId, { name: p.displayName ?? "", avatarMediaId: p.avatarMediaId ?? null });
    }
    return map;
  }, [thread.people]);

  const memberIds = useMemo(
    () => (thread.participants ?? []).map((p) => p.personId as string).filter(Boolean),
    [thread.participants],
  );

  const busy = add.isPending || remove.isPending;
  // SERVED, never a guess at the caller's role: the audience is a band seat role and this screen stamps no
  // tenant, so the client could not compute it if it wanted to.
  const canManage = thread.canManageMembers === true;
  // Adding is its OWN dialog over this one (PO), so the roster reads as the chat's members and nothing else.
  const [pickerOpen, setPickerOpen] = useState(false);
  const closePicker = () => {
    setPickerOpen(false);
    setAdding([]);
    add.reset();
  };

  return (
    <>
    <ActionModal
      title="Members"
      cancelLabel="Close"
      size="md"
      onCancel={onClose}
      // A caller who may add gets "Add members" beside Close; everyone else gets Close alone (an empty chooser) —
      // absent rather than disabled, because a button that is always refused is the dead button.
      {...(canManage
        ? { confirmLabel: "Add members", onConfirm: () => setPickerOpen(true) }
        : { actions: [] })}
    >
      <div className={styles.memberPick}>
        {memberIds.map((id) => {
          const person = nameById.get(id);
          const isMe = id === myPersonId;
          return (
            <div key={id} className={styles.memberRow}>
              {/* The face + name open that person's profile over this dialog (ART-264); your own row is not a
                  door, and a person the directory could not resolve has no profile to open. */}
              {!isMe && person ? (
                <button
                  type="button"
                  className={styles.memberPerson}
                  aria-label={`View ${person.name}'s profile`}
                  onClick={() => openPersonProfile(id)}
                >
                  <PersonAvatar personId={id} avatarMediaId={person.avatarMediaId ?? null} initials={nameInitials(person.name)} size="sm" />
                  <span className={styles.memberName}>{person.name}</span>
                </button>
              ) : (
                <>
                  <PersonAvatar
                    personId={id}
                    avatarMediaId={person?.avatarMediaId ?? null}
                    initials={nameInitials(person?.name ?? "")}
                    size="sm"
                  />
                  <span className={styles.memberName}>{person?.name ?? "Member"}</span>
                </>
              )}
              {isMe ? (
                // Your own row: the word is Leave and never Remove — see the note above. Where leaving is
                // not on offer at all it stays the plain "You", rather than a disabled control implying the
                // act exists and is merely refused.
                onLeave ? (
                  <Button size="sm" variant="ghost" tone="danger" onClick={onLeave}>
                    Leave
                  </Button>
                ) : (
                  <span className={styles.memberVia}>You</span>
                )
              ) : canManage ? (
                <IconButton
                  icon={UserMinus}
                  label={`Remove ${person?.name ?? "this person"}`}
                  tone="danger"
                  size="sm"
                  disabled={busy}
                  onClick={() => remove.mutate(id)}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {remove.isError && (
        <p className={styles.dialogError} role="alert">
          {errorMessage(remove.error, "Couldn't remove from the chat.")}
        </p>
      )}
    </ActionModal>

    {pickerOpen && (
      <ActionModal
        title="Add members"
        confirmLabel={adding.length > 1 ? `Add ${adding.length}` : "Add"}
        confirmDisabled={adding.length === 0}
        loading={add.isPending}
        size="md"
        onCancel={closePicker}
        onConfirm={() => add.mutate({ personIds: adding }, { onSuccess: closePicker })}
      >
        {/* The SAME picker the "+ New" door uses (PO), so "pick some people" looks and behaves one way in the
            inbox. `exclude` is the current roster, so somebody already in the chat is not offered at all.
            Only reachable for a caller the server would accept. */}
        <MessageablePicker value={adding} onChange={setAdding} exclude={memberIds} />
        {add.isError && (
          <p className={styles.dialogError} role="alert">
            {errorMessage(add.error, "Couldn't add to the chat.")}
          </p>
        )}
      </ActionModal>
    )}
    </>
  );
}
